import axios from 'axios';
import axiosInstance from '../axiosConfig';

export type RegistrationKind = 'member' | 'non_member';

export interface MintedToken {
  token: string;
  kind: RegistrationKind;
  expires_at: string; // ISO 8601
  ttl_minutes: number;
}

export interface ValidatedToken {
  valid: boolean;
  kind: RegistrationKind;
  expires_at: number; // unix seconds (JWT exp)
}

export interface JoinResult {
  success: boolean;
  access_token: string | null;
  data: any;
}

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Public registration endpoints (validate, join) must NOT go through the shared
// axiosInstance: its response interceptor wipes auth and redirects to /login on
// ANY 401, but an expired QR legitimately returns 401 and we want to render that
// inline on the public /join page. Use a bare client with no interceptors.
const publicClient = axios.create({
  baseURL: BASE,
  timeout: 15000,
  withCredentials: true, // H6: store the httpOnly auth cookie that /join sets
  headers: { 'Content-Type': 'application/json' },
});

const registrationService = {
  /** Staff/admin only: mint a short-lived QR registration token of `kind`. */
  async mintToken(kind: RegistrationKind, ttlMinutes?: number): Promise<MintedToken> {
    const resp = await axiosInstance.post('/registration/qr-token', {
      kind,
      ...(ttlMinutes ? { ttl_minutes: ttlMinutes } : {}),
    });
    return resp.data as MintedToken;
  },

  /** Public: validate a scanned token before showing the join form. */
  async validateToken(token: string): Promise<ValidatedToken> {
    const resp = await publicClient.get('/registration/validate', { params: { token } });
    return resp.data as ValidatedToken;
  },

  /** Public + token-gated: create the household; returns a login token for the primary. */
  async join(token: string, household: any): Promise<JoinResult> {
    const resp = await publicClient.post('/registration/join', { token, household });
    return resp.data as JoinResult;
  },
};

export default registrationService;
